import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, FileX2, Search } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { Input } from '@components/Input';
import { PermissionGuard } from '@components/PermissionGuard';
import { SimpleTable as Table, TableCell, TableHeader, TableRow } from '@components/Table';
import { companyService } from '@services/companyService';
import { sefazCommunicationLogService } from '@services/sefazCommunicationLogService';
import { userPreferencesService } from '@services/userPreferencesService';
import { Company, formatCnpj } from '@/types/company';
import {
  SefazCommunicationLog,
  SefazServiceType,
  SEFAZ_SERVICE_TYPE_OPTIONS,
  getSefazServiceTypeLabel,
} from '@/types/sefazCommunicationLog';
import { getInvoiceModelLabel } from '@/types/invoice';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const formatExecutionTime = (ms?: number | null) => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

export const SefazCommunicationLogListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<SefazCommunicationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [serviceType, setServiceType] = useState<SefazServiceType | ''>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [serie, setSerie] = useState<string>('');
  const [accessKey, setAccessKey] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [xmlExporting, setXmlExporting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Load user preferences
  useEffect(() => {
    interface SefazPrefs {
      company_id?: number;
      service_type?: SefazServiceType | '';
      invoice_number?: string;
      serie?: string;
      access_key?: string;
      date_from?: string;
      date_to?: string;
    }
    userPreferencesService.getPreferences<SefazPrefs>('FISCAL_SEFAZ_COMUNICATION').then((prefs) => {
      if (prefs) {
        if (prefs.company_id !== undefined) setCompanyId(prefs.company_id);
        if (prefs.service_type !== undefined) setServiceType(prefs.service_type);
        if (prefs.invoice_number !== undefined) setInvoiceNumber(prefs.invoice_number);
        if (prefs.serie !== undefined) setSerie(prefs.serie);
        if (prefs.access_key !== undefined) setAccessKey(prefs.access_key);
        if (prefs.date_from !== undefined) setDateFrom(prefs.date_from);
        if (prefs.date_to !== undefined) setDateTo(prefs.date_to);
      }
      setPreferencesLoaded(true);
    });
  }, []);

  useEffect(() => {
    companyService
      .getCompanies()
      .then((data) => {
        setCompanies(data);
        if (data.length === 1 && !companyId) setCompanyId(data[0].company_id);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    if (!companyId) {
      setError('Selecione uma empresa para consultar os logs.');
      setItems([]);
      setTotalPages(1);
      setTotalItems(0);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await sefazCommunicationLogService.list({
        page: currentPage,
        pageSize,
        company_id: companyId,
        service_type: serviceType || undefined,
        invoice_number: invoiceNumber ? Number(invoiceNumber) : undefined,
        serie: serie ? Number(serie) : undefined,
        access_key: accessKey.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar logs SEFAZ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && preferencesLoaded) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, preferencesLoaded]);

  const handleSearch = () => {
    setCurrentPage(1);
    setSelectedIds(new Set());
    loadItems();
    // Save filter preferences
    userPreferencesService.savePreferences('FISCAL_SEFAZ_COMUNICATION', {
      company_id: companyId,
      service_type: serviceType,
      invoice_number: invoiceNumber,
      serie,
      access_key: accessKey.trim(),
      date_from: dateFrom,
      date_to: dateTo,
    }).catch(() => {});
  };

  return (
    <PermissionGuard
      permission="sefaz_communication_log.read"
      fallback={
        <Card>
          <CardContent>
            <p className="text-slate-600">Você não tem permissão para visualizar logs da SEFAZ.</p>
          </CardContent>
        </Card>
      }
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-h1">Comunicação SEFAZ</h1>
          <p className="text-slate-600">
            Histórico de comunicações com a SEFAZ (autorizações, cancelamentos, inutilizações, consultas).
          </p>
        </div>

        <Card>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  Empresa <span className="text-red-500">*</span>
                </label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={companyId ?? ''}
                  onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">Selecione...</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>
                      {c.name} — {formatCnpj(c.document)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Tipo</label>
                <select
                  className="input-field w-full text-sm px-3 py-2"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value as SefazServiceType | '')}
                >
                  <option value="">Todos</option>
                  {SEFAZ_SERVICE_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Nº Nota"
                type="number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
              />
              <Input
                label="Série"
                type="number"
                value={serie}
                onChange={(e) => setSerie(e.target.value)}
              />
              <Input
                label="Chave de acesso"
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value.replace(/\D/g, '').slice(0, 44))}
                maxLength={44}
                placeholder="44 dígitos"
              />
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">De</label>
                <DateInputBR
                  className="input-field w-full text-sm px-3 py-2"
                  value={dateFrom}
                  onChange={setDateFrom}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Até</label>
                <DateInputBR
                  className="input-field w-full text-sm px-3 py-2"
                  value={dateTo}
                  onChange={setDateTo}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleSearch} fullWidth icon={<Search size={16} />}>
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export button */}
        {items.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {selectedIds.size > 0
                ? `${selectedIds.size} registro(s) selecionado(s)`
                : 'Selecione registros para exportar XMLs de resposta'}
            </p>
            <Button
              variant="outline"
              size="sm"
              icon={<Download size={16} />}
              disabled={selectedIds.size === 0 || xmlExporting}
              onClick={async () => {
                setXmlExporting(true);
                try {
                  await sefazCommunicationLogService.exportResponseXmlZip(Array.from(selectedIds));
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Erro ao exportar XMLs');
                } finally {
                  setXmlExporting(false);
                }
              }}
            >
              {xmlExporting ? 'Exportando...' : 'Exportar XMLs Response'}
            </Button>
          </div>
        )}

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-center w-10">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={items.length > 0 && items.every((i) => selectedIds.has(i.sefaz_log_id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(items.map((i) => i.sefaz_log_id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                    />
                  </th>
                  <th className="text-left">Data</th>
                  <th className="text-left">Empresa</th>
                  <th className="text-left">Tipo</th>
                  <th className="text-left">Modelo</th>
                  <th className="text-left">Série</th>
                  <th className="text-left">Nº Nota</th>
                  <th className="text-left">cStat</th>
                  <th className="text-left">xMotivo</th>
                  <th className="text-right">Tempo</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={11}>
                        <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-10 text-center text-slate-500">
                      <FileX2 className="mx-auto mb-2 text-slate-300" size={32} />
                      {companyId
                        ? 'Nenhum log encontrado para os filtros.'
                        : 'Selecione uma empresa para consultar.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.sefaz_log_id}>
                      <TableCell className="text-center w-10">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={selectedIds.has(item.sefaz_log_id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) {
                              next.add(item.sefaz_log_id);
                            } else {
                              next.delete(item.sefaz_log_id);
                            }
                            setSelectedIds(next);
                          }}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(item.created_at)}</TableCell>
                      <TableCell>
                        <p className="text-slate-800 text-sm">{item.company_name || `#${item.company_id}`}</p>
                        {item.company_document && (
                          <p className="text-xs text-slate-500 font-mono">{formatCnpj(item.company_document)}</p>
                        )}
                      </TableCell>
                      <TableCell>{getSefazServiceTypeLabel(item.service_type)}</TableCell>
                      <TableCell>{getInvoiceModelLabel(item.model)}</TableCell>
                      <TableCell className="font-mono text-sm">{item.serie ?? '—'}</TableCell>
                      <TableCell className="font-mono text-sm">{item.invoice_number ?? '—'}</TableCell>
                      <TableCell className="font-mono text-sm">{item.sefaz_status_code ?? '—'}</TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-xs">
                        <span className="block truncate" title={item.sefaz_status_description || ''}>
                          {item.sefaz_status_description || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatExecutionTime(item.execution_time_ms)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`/settings/fiscal/sefaz-communication-logs/${item.sefaz_log_id}`)
                          }
                          icon={<Eye size={14} />}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm text-slate-600">
                Página {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};
